import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import { Button, message } from "antd";
import { Footer, LoadingIcon, Sidebar } from "../../../components";
import Dropdowns from "./Dropdowns";
import { getGraphData } from "../../../utils/api";
import DashboardContent from "./DashboardContent";

const SpinachAnalysisDashboard = ({
  currentOrg,
  setCurrentOrg,
  orgData,
  setOrgData,
  orgLoading,
  setOrgLoading,
  siteData,
  setSiteData,
  currentSite,
  setCurrentSite,
  siteLoading,
  setSiteLoading,
  currentCrop,
  setCurrentCrop,
  cropLoading,
  setCropLoading,
  cropData,
  setCropData,
  searchParams,
  setSearchParams,
  settingDefaults,
  setSettingDefaults,
}) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [siteMetadata, setSiteMetadata] = useState([]);
  const [currentGreenhouse, setCurrentGreenhouse] = useState(null);
  const [currentPlot, setCurrentPlot] = useState(null);
  const [plotData, setPlotData] = useState([]);
  const [greenhouseData, setGreenhouseData] = useState([]);
  const [currentDate, setCurrentDate] = useState(null);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [graphData, setGraphData] = useState({});
  const [allRowGraphData, setAllRowGraphData] = useState({});
  const [graphLoading, setGraphLoading] = useState(false);
  const [dateData, setDateData] = useState([]);
  const [activeTab, setActiveTab] = useState("leafSize");

  const [greenHouseLabel, setGreenHouseLabel] = useState("Greenhouse");
  const [blockLabel, setBlockLabel] = useState("Block");

  // Merge row date-wise
  const mergeRowAndDateWiseData = useCallback((data) => {
    const mergedData = {};
    Object.entries(data).forEach(([rowName, rowEntries]) => {
      rowEntries.forEach((entry) => {
        const { harvest_date, data: entryData } = entry;
        if (!mergedData[harvest_date]) {
          mergedData[harvest_date] = [];
        }
        mergedData[harvest_date].push({ rowName, ...(entryData || {}) });
      });
    });
    return mergedData;
  }, []);

  const fetchGraphData = useCallback(async () => {
    if (
      !currentOrg ||
      !currentSite?.id ||
      !currentCrop?.id ||
      !currentGreenhouse?.id
    ) {
      return;
    }
    setGraphLoading(true);
    try {
      const graphRes = await getGraphData(
        currentOrg,
        currentSite.id,
        currentCrop.id,
        null,
        null,
        currentGreenhouse.id,
      );
      const greenhouseRaw = graphRes?.data?.data || {};
      const data = greenhouseRaw?.[currentPlot?.name] || [];
      const grapDataDateWise = {};
      data.forEach((dateWiseData) => {
        grapDataDateWise[dateWiseData.harvest_date] = dateWiseData.data;
      });

      setGraphData(grapDataDateWise);
      setAllRowGraphData(mergeRowAndDateWiseData(greenhouseRaw));
    } catch (err) {
      const msg =
        err?.response?.data?.detail || "Oops! Error fetching graph data!";
      messageApi.open({ type: "error", content: msg });
    } finally {
      setGraphLoading(false);
    }
  }, [
    currentOrg,
    currentSite,
    currentCrop,
    currentGreenhouse,
    currentPlot,
    messageApi,
    mergeRowAndDateWiseData,
  ]);

  useEffect(() => {
    if (currentPlot && currentDate && !dropdownLoading) {
      fetchGraphData();
    }
  }, [currentPlot, currentDate, dropdownLoading]);

  const isParentDataReady = useMemo(() => {
    if (!currentOrg || !currentSite || !currentCrop || !currentGreenhouse)
      return false;
    if (dropdownLoading) return false;
    return !graphLoading;
  }, [
    currentOrg,
    currentSite,
    currentCrop,
    currentGreenhouse,
    dropdownLoading,
    graphLoading,
  ]);

  return (
    <>
      {contextHolder}
      <div id="dashboard">
        <Sidebar
          active="spinach_analysis"
          open={sidebarOpen}
          setOpen={setSidebarOpen}
          sidebarType="SpinachGrower"
        />

        <div
          className={`flex w-full flex-col pb-14 pt-8 lg:pt-6 sm:pt-4 ${
            sidebarOpen ? "pl-[210px] lg:pl-[50px]" : "pl-[50px]"
          } transition-[padding] duration-200 ease-in-out`}
        >
          <div className="mx-auto w-full max-w-[1900px] px-8 xl:px-6 lg:px-3 md:px-2 sm:px-1.5">
            <Dropdowns
              siteMetadata={siteMetadata}
              setSiteMetadata={setSiteMetadata}
              currentOrg={currentOrg}
              setCurrentOrg={setCurrentOrg}
              orgData={orgData}
              setOrgData={setOrgData}
              orgLoading={orgLoading}
              setOrgLoading={setOrgLoading}
              currentSite={currentSite}
              setCurrentSite={setCurrentSite}
              siteData={siteData}
              setSiteData={setSiteData}
              siteLoading={siteLoading}
              setSiteLoading={setSiteLoading}
              currentCrop={currentCrop}
              setCurrentCrop={setCurrentCrop}
              cropData={cropData}
              setCropData={setCropData}
              cropLoading={cropLoading}
              setCropLoading={setCropLoading}
              currentPlot={currentPlot}
              setCurrentPlot={setCurrentPlot}
              currentGreenhouse={currentGreenhouse}
              setCurrentGreenhouse={setCurrentGreenhouse}
              greenhouseData={greenhouseData}
              setGreenhouseData={setGreenhouseData}
              plotData={plotData}
              setPlotData={setPlotData}
              currentDate={currentDate}
              setCurrentDate={setCurrentDate}
              setDropdownLoading={setDropdownLoading}
              searchParams={searchParams}
              setSearchParams={setSearchParams}
              settingDefaults={settingDefaults}
              setSettingDefaults={setSettingDefaults}
              dateData={dateData}
              setDateData={setDateData}
              greenHouseLabel={greenHouseLabel}
              setGreenHouseLabel={setGreenHouseLabel}
              blockLabel={blockLabel}
              setBlockLabel={setBlockLabel}
            />

            {Object.keys(siteMetadata).length === 0 && !dropdownLoading ? (
              <div className="text-2xl text-center">
                Metadata not Configured!
              </div>
            ) : (
              <div className="flex flex-col">
                {isParentDataReady ? (
                  <DashboardContent
                    currentOrg={currentOrg}
                    currentSite={currentSite}
                    graphData={graphData}
                    plotData={plotData}
                    allRowGraphData={allRowGraphData}
                    dropdownLoading={dropdownLoading}
                    dateList={dateData}
                    siteMetadata={siteMetadata}
                    currentCrop={currentCrop}
                    currentGreenhouse={currentGreenhouse}
                    currentPlot={currentPlot}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    greenHouseLabel={greenHouseLabel}
                    blockLabel={blockLabel}
                  />
                ) : (
                  <LoadingIcon
                    classname="absolute !h-[80vh] !w-[90vw]"
                    height="200px"
                  />
                )}
              </div>
            )}
          </div>
        </div>
        <Footer sidebarOpen={sidebarOpen} />
      </div>
    </>
  );
};

export default SpinachAnalysisDashboard;
